import { PageLoader } from './PageLoader'
import { SkeletonCard } from './SkeletonLoaders'
import { cn } from '@/lib/utils'

interface DashboardPageLoaderProps {
  message?: string
  showSkeleton?: boolean
  className?: string
}

/**
 * Specialized loading component for dashboard pages that provides
 * a more contextual loading experience with skeleton content
 */
export function DashboardPageLoader({ 
  message = 'Loading...', 
  showSkeleton = false,
  className 
}: DashboardPageLoaderProps) {
  if (showSkeleton) {
    return (
      <div className={cn('space-y-4 sm:space-y-6', className)}>
        {/* Page header skeleton */}
        <div className="space-y-2">
          <div className="h-8 bg-lilac-100 rounded w-48 animate-pulse" />
          <div className="h-4 bg-lilac-100 rounded w-64 animate-pulse" />
        </div>
        
        {/* Content skeleton */}
        <div className="space-y-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    )
  }

  return (
    <PageLoader 
      message={message}
      className={className}
    />
  )
}

/**
 * Loading fallback for calendar page
 */
export function CalendarPageLoader() {
  return (
    <DashboardPageLoader 
      message="Loading your calendar..."
      showSkeleton={true}
    />
  )
}

/**
 * Loading fallback for booking requests page
 */
export function BookingRequestsPageLoader() {
  return (
    <DashboardPageLoader 
      message="Loading booking requests..."
      showSkeleton={true}
    />
  )
}

/**
 * Loading fallback for bookings page
 */
export function BookingsPageLoader() {
  return (
    <DashboardPageLoader 
      message="Loading your bookings..."
      showSkeleton={true}
    />
  )
}

/**
 * Loading fallback for public profile page
 */
export function PublicProfilePageLoader() {
  return (
    <div className="min-h-screen bg-linen flex flex-col">
      <div className="max-w-4xl mx-auto container-responsive py-4 sm:py-6 lg:py-8">
        <PageLoader 
          message="Loading caregiver profile..."
          className="min-h-[400px]"
        />
      </div>
    </div>
  )
}